import type {
  WishlistAvailabilityStatus,
  WishlistCompany,
} from "@/types/inboxWishlist";

export const WISHLIST_PAGE_SIZE = 5;

export type WishlistSegment =
  "all" | "action_needed" | "tracking_ready" | "unavailable";
export type WishlistStatusFilter = "all" | WishlistAvailabilityStatus;

export interface WishlistFilters {
  industry: string;
  query: string;
  segment: WishlistSegment;
  status: WishlistStatusFilter;
}

export const DEFAULT_WISHLIST_FILTERS: Readonly<WishlistFilters> =
  Object.freeze({
    industry: "all",
    query: "",
    segment: "all",
    status: "all",
  });

function normalized(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function matchesSegment(
  company: WishlistCompany,
  segment: WishlistSegment,
): boolean {
  if (segment === "all") return true;
  if (segment === "unavailable")
    return company.availability.status === "unavailable";
  return company.availability.status === "ready_to_add";
}

function searchText(company: WishlistCompany): string {
  return normalized(
    [
      company.company,
      company.industry,
      company.website,
      company.employee_band,
      ...company.locations,
    ]
      .filter(Boolean)
      .join(" "),
  );
}

export function filterWishlistCompanies(
  companies: readonly WishlistCompany[],
  filters: Readonly<WishlistFilters>,
): WishlistCompany[] {
  const query = normalized(filters.query);
  return companies
    .filter((company) => matchesSegment(company, filters.segment))
    .filter(
      (company) =>
        filters.status === "all" ||
        company.availability.status === filters.status,
    )
    .filter(
      (company) =>
        filters.industry === "all" || company.industry === filters.industry,
    )
    .filter((company) => !query || searchText(company).includes(query))
    .sort((left, right) => {
      const createdOrder = (right.created_at ?? "").localeCompare(
        left.created_at ?? "",
      );
      return createdOrder || left.company.localeCompare(right.company);
    });
}

export function paginateWishlistCompanies(
  companies: readonly WishlistCompany[],
  page: number,
  pageSize = WISHLIST_PAGE_SIZE,
): WishlistCompany[] {
  const safeSize = Math.max(1, Math.trunc(pageSize));
  const safePage = Math.max(1, Math.trunc(page));
  const start = (safePage - 1) * safeSize;
  return companies.slice(start, start + safeSize);
}

export function wishlistIndustries(
  companies: readonly WishlistCompany[],
): string[] {
  return [
    ...new Set(
      companies
        .map((company) => company.industry?.trim())
        .filter(Boolean) as string[],
    ),
  ].sort((left, right) => left.localeCompare(right));
}

function utcDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function formatWishlistDate(
  value: string | null,
  now = new Date(),
): string {
  if (!value) return "Not checked yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not checked yet";
  const elapsedDays = Math.max(
    0,
    Math.floor((utcDay(now) - utcDay(date)) / 86_400_000),
  );
  if (elapsedDays === 0) return "Today";
  if (elapsedDays === 1) return "Yesterday";
  return `${elapsedDays} days ago`;
}
